import type { ElationPatient } from "~/bridge/types";
import {
  EHR_SOURCE_SYSTEMS,
  type EhrSourceSystem,
} from "../graphql/resolveEhrMember";

export interface EhrPatientContext {
  sourceSystem: EhrSourceSystem;
  ehrPatientId?: string;
  firstName?: string;
  lastName?: string;
  dob?: string;
  capturedAt: number;
  /**
   * Whether the Elation chart was scraped from the ACTIVE (foreground) tab.
   * Auto-navigation is gated on this so the app never jumps to a patient open
   * only in a background tab the clinician isn't looking at.
   */
  sourceTabActive?: boolean;
}

export function elationPatientToContext(
  data: ElationPatient | null,
  capturedAt: number,
  sourceTabActive = false,
): EhrPatientContext | null {
  if (!data) return null;
  const ehrPatientId = data.ehrPatientId ?? undefined;
  if (!ehrPatientId) return null;
  const firstName = data.firstName ?? undefined;
  const lastName = data.lastName ?? undefined;
  // The source system is scraped by whichever adapter matched the tab and rides
  // the bridge payload. Validate the untrusted wire value against the known set
  // and default to Elation (the original, always-on integration) when absent or
  // unrecognized, so a stale extension that predates this field still resolves.
  const sourceSystem: EhrSourceSystem = EHR_SOURCE_SYSTEMS.includes(
    data.sourceSystem as EhrSourceSystem,
  )
    ? (data.sourceSystem as EhrSourceSystem)
    : "EMR_ELATION";
  return {
    sourceSystem,
    ehrPatientId,
    firstName,
    lastName,
    dob: data.dob ?? undefined,
    capturedAt,
    sourceTabActive,
  };
}

/**
 * Whether the "In Elation" context card should be rendered on the Member Search
 * page. Sticky scrape data deliberately survives a benign tab switch (so the
 * card doesn't blank on every focus change and we avoid a re-scrape round-trip),
 * but the spec requires HIDING the card once the clinician has navigated away
 * from the chart — i.e. it is no longer the foreground tab (`sourceTabActive`).
 * We suppress RENDERING rather than wipe the sticky data, so returning to the
 * foreground chart re-shows the card immediately without re-scraping.
 */
export function shouldShowPatientContext(
  ctx: EhrPatientContext | null,
): ctx is EhrPatientContext {
  return ctx !== null && ctx.sourceTabActive === true;
}
